import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../api/client'
import type { AppConfig, Job, Study } from '../types'
import EmptyState from './ui/EmptyState'
import LoadingSkeleton from './ui/LoadingSkeleton'
import RecentActivity from './RecentActivity'
import StatusBadge from './ui/StatusBadge'
import { formatDuration } from '../utils/time'
import type { LucideIcon } from 'lucide-react'
import {
  FileUp,
  ClipboardList,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  FileText,
  RefreshCw,
  Clock,
} from 'lucide-react'

function HomePage() {
  const navigate = useNavigate()
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [studies, setStudies] = useState<Study[]>([])
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadDashboard = async () => {
    setLoading(true)
    setError(null)
    try {
      const [configData, studiesData, jobsData] = await Promise.all([
        apiClient.get<AppConfig>('/config'),
        apiClient.get<{ items: Study[] }>('/studies', { params: { page_size: '50' } }),
        apiClient.get<Job[] | { items?: Job[] }>('/jobs/recent', { params: { limit: '50' } }),
      ])
      setConfig(configData)
      setStudies(studiesData.items || [])
      setJobs(Array.isArray(jobsData) ? jobsData : (jobsData.items || []))
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load dashboard'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    loadDashboard().then(() => cancelled)
    return () => { cancelled = true }
  }, [])

  const failedJobs = jobs.filter((j) => j.status === 'failed')
  const runningJobs = jobs.filter((j) => j.status === 'running' || j.status === 'pending')
  const reviewJobs = jobs.filter((j) => j.status === 'needs_review')
  const studiesWithProtocols = studies.filter((study) => study.protocol_id !== null)
  const recentStudies = [...studies]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 8)

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {config ? `Protocol Decompiler v${config.app_version} · ${config.environment}` : 'Loading…'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadDashboard} className="btn-secondary" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} aria-hidden={true} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="panel p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden={true} />
          <div className="text-sm">{error}</div>
        </div>
      )}

      {loading ? (
        <LoadingSkeleton rows={8} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={FileText}
              label="Studies"
              value={studies.length}
              onClick={() => navigate('/studies')}
            />
            <StatCard
              icon={FileUp}
              label="With Protocols"
              value={studiesWithProtocols.length}
              onClick={() => navigate('/studies')}
            />
            <StatCard
              icon={ClipboardList}
              label="Jobs to Review"
              value={reviewJobs.length}
              status={reviewJobs.length > 0 ? 'warning' : 'success'}
              onClick={() => navigate('/studies')}
            />
            <StatCard
              icon={AlertCircle}
              label="Failed Jobs"
              value={failedJobs.length}
              status={failedJobs.length > 0 ? 'error' : 'default'}
              onClick={() => navigate('/admin')}
            />
          </div>

          {runningJobs.length > 0 && (
            <div className="panel p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white mb-2">
                <RefreshCw className="h-4 w-4 animate-spin text-indigo-500" aria-hidden={true} />
                {runningJobs.length} extraction job{runningJobs.length === 1 ? '' : 's'} in progress
              </div>
              <div className="space-y-2">
                {runningJobs.slice(0, 3).map((job) => (
                  <div
                    key={job.id}
                    onClick={() => job.study_id ? navigate(`/studies/${job.study_id}`) : undefined}
                    className={`flex items-center justify-between text-sm px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded ${job.study_id ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600' : ''}`}
                  >
                    <div className="min-w-0">
                      <span className="text-gray-700 dark:text-gray-300 truncate block">
                        {(((job.metadata_ as Record<string, unknown> | undefined) ?? (job.metadata as Record<string, unknown> | undefined))?.protocol_filename as string | undefined) || `Job ${job.id.slice(0, 8)}`}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDuration(job.elapsed_seconds ?? 0)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusBadge status={job.status} />
                      <span className="text-gray-500 dark:text-gray-400">{job.progress_percent}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="panel">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Recent Studies</h2>
                <button onClick={() => navigate('/studies')} className="text-sm text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center">
                  View all <ArrowRight className="h-4 w-4 ml-1" aria-hidden={true} />
                </button>
              </div>
              {recentStudies.length === 0 ? (
                <EmptyState
                  icon={FileUp}
                  title="No studies yet"
                  description="Create a study from the Studies page to get started."
                  action={
                    <button onClick={() => navigate('/studies')} className="btn-primary">
                      View Studies
                    </button>
                  }
                />
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {recentStudies.map((study) => (
                    <button
                      key={study.id}
                      onClick={() => navigate(`/studies/${study.id}`)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{study.study_name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {study.study_code || 'No code'} · {study.phase || 'No phase'}
                        </p>
                      </div>
                      {study.protocol_processing_status && study.protocol_processing_status !== 'failed' && (
                        <span className="ml-3 inline-flex shrink-0 items-center gap-1.5 text-xs text-primary-700 dark:text-primary-300">
                          <RefreshCw className="h-3 w-3 animate-spin" aria-hidden={true} /> Processing
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="panel">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Quick Actions</h2>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <QuickAction
                  icon={ClipboardList}
                  label="Open Studies"
                  description={`${studies.length} shared stud${studies.length === 1 ? 'y' : 'ies'}`}
                  onClick={() => navigate('/studies')}
                  disabled={studies.length === 0}
                />
                <QuickAction
                  icon={CheckCircle2}
                  label="Processing Jobs"
                  description={`${runningJobs.length} job${runningJobs.length === 1 ? '' : 's'} in progress`}
                  onClick={() => navigate('/studies')}
                  disabled={runningJobs.length === 0}
                />
              </div>
            </div>
          </div>

          <RecentActivity limit={20} />
        </>
      )}
    </div>
  )
}

interface StatCardProps {
  icon: LucideIcon
  label: string
  value: number
  status?: 'default' | 'success' | 'warning' | 'error'
  onClick?: () => void
}

function StatCard({ icon: Icon, label, value, status = 'default', onClick }: StatCardProps) {
  const statusClasses = {
    default: 'text-gray-900 dark:text-white',
    success: 'text-green-700 dark:text-green-300',
    warning: 'text-amber-700 dark:text-amber-300',
    error: 'text-red-700 dark:text-red-300',
  }
  return (
    <button
      onClick={onClick}
      className="panel p-4 flex items-center gap-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors w-full"
    >
      <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
        <Icon className="h-5 w-5 text-gray-600 dark:text-gray-300" aria-hidden={true} />
      </div>
      <div>
        <p className={`text-2xl font-bold leading-none ${statusClasses[status]}`}>{value}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{label}</p>
      </div>
    </button>
  )
}

interface QuickActionProps {
  icon: LucideIcon
  label: string
  description: string
  onClick: () => void
  disabled?: boolean
}

function QuickAction({ icon: Icon, label, description, onClick, disabled }: QuickActionProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
    >
      <Icon className="h-5 w-5 text-primary-600 mt-0.5" aria-hidden={true} />
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      </div>
    </button>
  )
}

export default HomePage
